import { useEffect, useRef, useState } from "react";

const features = [
  { label: "Solar Inverter Setup" },
  { label: "Battery Storage Solutions" },
  { label: "Solar Material Financing" },
  { label: "24 X 7 Call & Chat Support" },
  { label: "Proven Track Record" },
  { label: "Customer-Centric Approach" },
];

function CheckIcon() {
  return (
    <svg
      className="w-5 h-5 text-white flex-shrink-0"
      viewBox="0 0 20 20"
      fill="currentColor"
    >
      <path
        fillRule="evenodd"
        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function BoltIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M13 2L4.09 12.97H11L10 22l8.91-10.97H13L13 2z" />
    </svg>
  );
}

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

export default function AboutSection() {
  const [sectionRef, inView] = useInView(0.1);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;600;700;800&display=swap');

        .about-section * { font-family: 'Manrope', sans-serif; }

        /* Fade + slide up */
        .anim-fade { opacity: 0; transform: translateY(32px); transition: opacity 0.7s ease, transform 0.7s ease; }
        .anim-fade.visible { opacity: 1; transform: translateY(0); }

        /* Slide in from left */
        .anim-left { opacity: 0; transform: translateX(-48px); transition: opacity 0.8s ease, transform 0.8s ease; }
        .anim-left.visible { opacity: 1; transform: translateX(0); }

        /* Slide in from right */
        .anim-right { opacity: 0; transform: translateX(48px); transition: opacity 0.8s ease, transform 0.8s ease; }
        .anim-right.visible { opacity: 1; transform: translateX(0); }

        /* Stagger delays */
        .delay-0 { transition-delay: 0ms; }
        .delay-1 { transition-delay: 120ms; }
        .delay-2 { transition-delay: 220ms; }
        .delay-3 { transition-delay: 320ms; }
        .delay-4 { transition-delay: 420ms; }
        .delay-5 { transition-delay: 520ms; }
        .delay-6 { transition-delay: 620ms; }
        .delay-7 { transition-delay: 700ms; }
        .delay-8 { transition-delay: 800ms; }

        /* Floating animation for images */
        @keyframes floatA {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        @keyframes floatB {
          0%, 100% { transform: translateY(0px) scale(1); }
          50% { transform: translateY(-7px) scale(1.01); }
        }
        .float-a { animation: floatA 5s ease-in-out infinite; }
        .float-b { animation: floatB 6s ease-in-out infinite 1s; }

        /* Pulse for green dot */
        @keyframes pulse-dot {
          0%, 100% { box-shadow: 0 0 0 0 rgba(74, 222, 128, 0.5); }
          50% { box-shadow: 0 0 0 8px rgba(74, 222, 128, 0); }
        }
        .pulse-dot { animation: pulse-dot 2s ease-in-out infinite; }

        /* Feature chip hover */
        .feature-chip { transition: transform 0.2s ease, box-shadow 0.2s ease; }
        .feature-chip:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(74, 222, 128, 0.2); }

        /* Dot grid */
        .dot-grid {
          background-image: radial-gradient(circle, #d1d5db 1.2px, transparent 1.2px);
          background-size: 18px 18px;
        }
      `}</style>

      <section
        ref={sectionRef}
        className="about-section bg-white py-16 px-6 md:px-10 lg:px-12 xl:px-20 overflow-hidden"
      >
        <div className=" flex flex-col lg:flex-row items-center gap-12 lg:gap-20">

          {/* ── LEFT: Images ── */}
          <div className={`relative w-full lg:w-[520px] flex-shrink-0 h-[420px] anim-left ${inView ? "visible" : ""} delay-0`}>

            {/* Dot grid background */}
            <div className="dot-grid absolute top-4 right-0 w-56 h-64 opacity-60 rounded-xl" />

            {/* Main large image */}
            <div className="float-a absolute top-0 left-0 w-[300px] md:w-[440px] rounded-3xl overflow-hidden shadow-2xl border-4 border-white z-10">
              <img
                src="https://images.unsplash.com/photo-1509391366360-2e959784a276?w=700&q=80"
                alt="Solar panels at sunset"
                className="w-full h-[360px] object-cover"
              />
            </div>

            {/* Smaller overlapping image */}
            <div className="float-b absolute bottom-0 right-0 w-[220px] md:w-[360px] lg:w-[400px] rounded-2xl overflow-hidden shadow-2xl border-4 border-white z-20">
              <img
                src="https://images.unsplash.com/photo-1570001000000-32c1c2a9e0b5?w=500&q=80"
                alt="Solar panels on rooftop"
                className="w-full h-[300px] object-cover"
                onError={e => {
                  e.target.src = "https://images.unsplash.com/photo-1497440001374-f26997328c1b?w=500&q=80";
                }}
              />
            </div>

            {/* Green pulse dot accent */}
            
          </div>

          {/* ── RIGHT: Content ── */}
          <div className="flex-1 min-w-0">
            {/* Label */}
            <div className={`flex items-center gap-2 mb-3 anim-fade ${inView ? "visible" : ""} delay-1`}>
              <span className="text-green-500">
                <BoltIcon />
              </span>
              <span className="text-green-600 text-sm font-rajdhani font-bold uppercase tracking-widest">
                About Us
              </span>
            </div>

            {/* Heading */}
            <h2 className={`text-2xl md:text-4xl lg:text-[45px] font-rajdhani font-extrabold text-gray-900 leading-relaxed mb-5 anim-fade ${inView ? "visible" : ""} delay-2`}
              style={{ color: "#1a2e1a" }}>
              About Green Energy Solar
            </h2>

            {/* Paragraphs */}
            <p className={`text-gray-500 text-[15px] leading-relaxed mb-4 anim-fade ${inView ? "visible" : ""} delay-3`}>
              Vishwaraja Enterprises is a dynamic and customer-focused organization committed to delivering quality products and reliable services. Built on the principles of integrity, excellence, and innovation, we strive to meet the evolving needs of our customers while maintaining the highest standards in everything we do.

            </p>
            <p className={`text-gray-500 text-[15px] leading-relaxed mb-8 anim-fade ${inView ? "visible" : ""} delay-4`}>
             
With a strong emphasis on customer satisfaction, Vishwaraja Enterprises aims to build long-term relationships based on trust and performance. Our team is dedicated to continuous improvement, ensuring that we provide efficient solutions and add value to every project we undertake.
            </p>

            {/* Feature grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
              {features.map((f, i) => (
                <div
                  key={f.label}
                  className={`feature-chip flex items-center gap-3 anim-fade ${inView ? "visible" : ""}`}
                  style={{ transitionDelay: `${(i + 5) * 100}ms` }}
                >
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 flex items-center justify-center shadow-md">
                    <CheckIcon />
                  </span>
                  <span className="text-gray-800 font-bold text-[14.5px]">{f.label}</span>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>
    </>
  );
}